import { useMemo } from 'react'
import { FileSearch, MoreVertical, Pencil, Trash2 } from 'lucide-react'
import {
  DataTable,
  type DataTableColumn,
  EmptyState,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Button,
  type DataTablePagination,
} from '@atrvd/saas-ui'
import { cn } from '@atrvd/saas-ui'
import {
  formatCategoryAmount,
  formatDate,
  formatDateTime,
} from '@dcp/shared/utils/formatters'
import { maskAccountNumber } from '@/lib/utils'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { queryErrorDisplay, type TableQueryStateProps } from '@/components/common/queryErrorDisplay'
import type { TenantTransaction } from '@/lib/db-tenant'

interface ManualTransactionsTableProps extends TableQueryStateProps {
  transactions: TenantTransaction[]
  onClearFilters?: () => void
  onEdit?: (tx: TenantTransaction) => void
  onDelete?: (tx: TenantTransaction) => void
  /**
   * The footer, owned by the page (`useTablePagination().dataTablePagination`);
   * `undefined` hides it (≤ 20 rows, or the total not known yet).
   */
  pagination: DataTablePagination | undefined
}

/**
 * Column definitions for the manual transactions table.
 * Matches the AC: Facility, Transaction Date, Bank, Account Name, Account Number,
 * Transaction Category, Amount, Description, Created Timestamp, User, Actions
 */
const columns: DataTableColumn<TenantTransaction>[] = [
  {
    // DCP-324: the facility is the ACCOUNT's, read through the embed. The
    // transaction's own `facility_id` — a copy stamped at INSERT that no
    // reassignment refreshed — is gone, which is what made a reassigned
    // account's history keep showing the facility it had on the day it was
    // written (or "-" forever, when it had none).
    //
    // `key` deliberately keeps its old value even though no such field exists
    // any more: it is the identity this column has in the persisted
    // Columns-menu preferences (`useTableColumnPrefs`), and `reconcile` treats
    // an unrecognised key as a NEW column — appended last and HIDDEN by
    // default. Renaming it would silently remove the Facility column for every
    // user who has ever touched that menu, to fix nothing.
    key: 'facility_id',
    header: 'Facility',
    render: (tx) => tx.bank_accounts?.facilities?.name ?? '-',
    cellClassName: 'font-medium',
  },
  {
    key: 'transaction_date',
    header: 'Transaction Date',
    render: (tx) => formatDate(tx.transaction_date),
    // Anchor column — always visible, cannot be hidden via the Columns menu.
    disableHide: true,
  },
  {
    key: 'bank_institution',
    header: 'Bank',
    render: (tx) => tx.bank_accounts?.institution_name ?? '-',
  },
  {
    key: 'bank_account_name',
    header: 'Account Name',
    render: (tx) => tx.bank_accounts?.name ?? '-',
  },
  {
    key: 'bank_account_number',
    header: 'Account Number',
    render: (tx) =>
      tx.bank_accounts?.account_number ? maskAccountNumber(tx.bank_accounts.account_number) : '-',
  },
  {
    key: 'category_id',
    header: 'Transaction Category',
    render: (tx) => tx.categories?.name ?? '-',
  },
  {
    key: 'amount',
    header: 'Amount',
    render: (tx) => {
      // DCP-250 Phase 2+: amount is stored signed; formatCategoryAmount
      // signs by value regardless of the second arg. The category type arg
      // is dropped here for clarity — it is no longer read by the formatter.
      const amt = formatCategoryAmount(tx.amount)
      return (
        <span className={cn('font-medium', amt.className)}>
          {amt.text}
        </span>
      )
    },
  },
  {
    key: 'description',
    header: 'Description',
    // DCP-361: same contract as BankTransactionsTable — `minWidth` is the column
    // floor on the <th>, `max-w` on the inner block is the cap that forces the
    // wrap (max-width on a table cell is ignored in table-layout:auto), and the
    // full text wraps over several lines instead of a one-line ellipsis.
    minWidth: '320px',
    render: (tx) => (
      <div className="max-w-[420px] whitespace-normal break-words">{tx.description || '-'}</div>
    ),
  },
  {
    key: 'notes',
    header: 'Notes',
    // DCP-169: read-only display of the transaction's free-text notes (stored
    // in metadata.notes, surfaced as `tx.notes` by db.manualTransactions.list).
    // Editing is intentionally out of scope here — it happens in the LOC popup
    // (a later piece); this column only renders the persisted value. Empty
    // notes render as blank (not '-') per the spec.
    render: (tx) => (
      <span className="max-w-[200px] truncate block" title={tx.notes}>
        {tx.notes || ''}
      </span>
    ),
  },
  {
    key: 'created_at',
    header: 'Created Timestamp',
    render: (tx) => formatDateTime(tx.created_at),
  },
  {
    key: 'user_id',
    header: 'User',
    render: (tx) => tx.profiles?.full_name ?? '-',
  },
]

/**
 * Table component for displaying manually entered transactions.
 * Used in the Transactions Import → Transactions Manual Input tab.
 */
export function ManualTransactionsTable({
  transactions,
  loading,
  refreshing,
  error,
  retrying,
  onRetry,
  onClearFilters,
  onEdit,
  onDelete,
  pagination,
}: ManualTransactionsTableProps) {
  /**
   * Full column set = base columns + the actions column. Memoized so the array
   * identity is stable across renders (it only changes when onEdit/onDelete
   * change), which keeps useTableColumnPrefs's key derivation cheap and avoids
   * unnecessary reconciliation work.
   */
  const allColumns: DataTableColumn<TenantTransaction>[] = useMemo(
    () => [
      ...columns,
      {
        key: 'actions' as keyof TenantTransaction,
        header: 'Actions',
        render: (tx) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label="Open menu"
              >
                <MoreVertical className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit?.(tx)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete?.(tx)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        width: '60px',
        // Edit/Delete controls must always be reachable — never hideable.
        disableHide: true,
      },
    ],
    [onEdit, onDelete]
  )

  // Per-user column visibility + order preferences (persisted in localStorage).
  const columnPrefs = useTableColumnPrefs('manual-transactions', allColumns)

  const { banner, tableError } = queryErrorDisplay({ error, retrying, onRetry }, transactions.length)

  return (
    <>
      {banner}
      <DataTable
        title="Manual Transactions"
        className="min-h-[680px]"
        columns={allColumns}
        data={transactions}
        loading={loading}
        refreshing={refreshing}
        error={tableError}
        density="compact"
        pagination={pagination}
        rowKey={(tx) => tx.id}
        // DCP-361: `align-top` is needed because the Description cell now wraps
        // over up to three lines — under the default `align-middle` the
        // single-line cells (Transaction Date, Amount, Facility) would float in
        // the vertical centre of a tall row. The compiled `[&>td]` selector
        // outranks a plain `align-middle` coming from a column's `cellClassName`
        // (specificity 0,1,1 vs 0,1,0, and they sit on different elements so
        // tailwind-merge cannot reconcile them), so it also top-aligns the
        // `actions` cell — its menu button sits at the top of a tall row, which
        // is deliberate and consistent. Use `cellClassName: '!align-middle'`
        // there if that is ever revisited.
        rowClassName="[&>td]:align-top"
        emptyState={
          <EmptyState
            icon={FileSearch}
            title="No manual transactions found"
            description="No manually entered transactions match the current filters"
            action={onClearFilters ? { label: 'Clear Filters', onClick: onClearFilters } : undefined}
          />
        }
        itemName="transactions"
        enableColumnReorder
        {...columnPrefs}
      />
    </>
  )
}
